// US consumer price benchmarks used to calculate and display savings on
// product detail pages.
//
// POLICY (2026-04 audit): For ingredients with a US generic available, we
// compare against the GENERIC retail cash price at chain pharmacies
// (CVS/Walgreens/Walmart) — WITHOUT coupons or discount programs. This
// represents what a typical uninsured or elderly consumer actually pays
// when walking into a chain pharmacy with a prescription.
//
// Why cash price without coupons? Because despite tools like GoodRx,
// SingleCare, and Cost Plus Drugs existing, the majority of cash-paying
// customers don't use them. In 2025 only ~6M Americans were monthly
// active GoodRx users against ~330M population. 60% of adults say they
// worry about drug costs; 42% have been prescribed something they could
// not afford. The chain retail cash price is the financial reality for
// many Americans.
//
// Where a US generic doesn't exist (Stendra, Ozempic, Wegovy, Januvia,
// Farxiga, Jardiance), we use the brand retail cash price and set
// isBrandBaseline=true so the UI labels accordingly.
//
// Where the drug is not sold in the US at all (Priligy/dapoxetine,
// Zydena/udenafil, combination PDE5+dapoxetine tablets), we use an
// EU/Asia retail reference and flag the source accordingly.
//
// Prices are approximate 2026 averages sourced from GoodRx "average
// retail price" fields, SingleCare retail pricing, and chain pharmacy
// listings. Review annually. Intentionally rounded to stay conservative.

export interface PriceBenchmark {
  usaBrand: string;        // Brand name (e.g., "Viagra") — kept as a recognition anchor
  manufacturer: string;    // Brand manufacturer — for recognition
  retailPrice: number;     // per-unit retail cash price in USD (generic when available)
  unit: string;            // "tablet", "capsule", "tube", "pen", etc.
  dosage: string;          // "100mg", "5g", etc. — what the unit represents
  source: string;          // Citation shorthand — describes what the price represents
  isBrandBaseline?: boolean; // true when no US generic exists → UI shows brand name raw
                             // false/undefined → UI shows "Generic {brand}" prefix
}

// Keyed by subcategory slug (same slugs used in product.categories[1])
// Falls back to class-level benchmark for non-specific subcats.
const BENCHMARKS: Record<string, PriceBenchmark> = {
  // ─── Sexual Health — PDE5 inhibitors ───
  // Generic tadalafil/sildenafil/vardenafil exist in US. Chain cash prices
  // without coupon remain high — most uninsured consumers face these.
  sildenafil: {
    usaBrand: 'Viagra',
    manufacturer: 'Pfizer',
    retailPrice: 7,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Generic sildenafil @ US chain pharmacy cash',
  },
  tadalafil: {
    usaBrand: 'Cialis',
    manufacturer: 'Eli Lilly',
    retailPrice: 18,
    unit: 'tablet',
    dosage: '20mg',
    source: 'Generic tadalafil @ US chain pharmacy cash',
  },
  vardenafil: {
    usaBrand: 'Levitra',
    manufacturer: 'Bayer',
    retailPrice: 15,
    unit: 'tablet',
    dosage: '20mg',
    source: 'Generic vardenafil @ US chain pharmacy cash',
  },
  avanafil: {
    usaBrand: 'Stendra',
    manufacturer: 'Metuchen',
    retailPrice: 65,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Brand Stendra — no US generic yet',
    isBrandBaseline: true,
  },
  udenafil: {
    usaBrand: 'Zydena',
    manufacturer: 'Mezzion Pharma',
    retailPrice: 30,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Korean pharmacy retail — not sold in US',
    isBrandBaseline: true,
  },
  dapoxetine: {
    usaBrand: 'Priligy',
    manufacturer: 'Menarini',
    retailPrice: 20,
    unit: 'tablet',
    dosage: '60mg',
    source: 'EU/Asia retail — not FDA approved',
    isBrandBaseline: true,
  },
  'sildenafil-delayed': {
    usaBrand: 'Viagra + Priligy',
    manufacturer: 'Pfizer / Menarini',
    retailPrice: 48,
    unit: 'tablet',
    dosage: '100mg + 60mg',
    source: 'Combined brand retail — not sold as combo in US',
    isBrandBaseline: true,
  },
  'tadalafil-delayed': {
    usaBrand: 'Cialis + Priligy',
    manufacturer: 'Eli Lilly / Menarini',
    retailPrice: 38,
    unit: 'tablet',
    dosage: '20mg + 60mg',
    source: 'Combined brand retail — not sold as combo in US',
    isBrandBaseline: true,
  },
  'vardenafil-delayed': {
    usaBrand: 'Levitra + Priligy',
    manufacturer: 'Bayer / Menarini',
    retailPrice: 42,
    unit: 'tablet',
    dosage: '20mg + 60mg',
    source: 'Combined brand retail — not sold as combo in US',
    isBrandBaseline: true,
  },
  'avanafil-delayed': {
    usaBrand: 'Stendra + Priligy',
    manufacturer: 'Metuchen / Menarini',
    retailPrice: 85,
    unit: 'tablet',
    dosage: '100mg + 60mg',
    source: 'Combined brand retail — not sold as combo in US',
    isBrandBaseline: true,
  },

  // ─── Hair Loss ───
  finasteride: {
    usaBrand: 'Propecia',
    manufacturer: 'Merck',
    retailPrice: 2,
    unit: 'tablet',
    dosage: '1mg',
    source: 'Generic finasteride @ US chain pharmacy cash',
  },
  dutasteride: {
    usaBrand: 'Avodart',
    manufacturer: 'GSK',
    retailPrice: 5.50,
    unit: 'capsule',
    dosage: '0.5mg',
    source: 'Generic dutasteride @ US chain pharmacy cash',
  },
  minoxidil: {
    usaBrand: 'Rogaine',
    manufacturer: 'Johnson & Johnson',
    retailPrice: 15,
    unit: 'bottle',
    dosage: '5% / 60ml',
    source: 'Generic minoxidil 5% @ US retail (OTC)',
  },
  aldactone: {
    usaBrand: 'Aldactone',
    manufacturer: 'Pfizer',
    retailPrice: 0.70,
    unit: 'tablet',
    dosage: '25mg',
    source: 'Generic spironolactone @ US chain pharmacy cash',
  },

  // ─── Skin Care / Acne ───
  roaccutane: {
    usaBrand: 'Accutane',
    manufacturer: 'Roche',
    retailPrice: 13,
    unit: 'capsule',
    dosage: '20mg',
    source: 'Generic isotretinoin @ US chain pharmacy cash',
  },
  retinol: {
    usaBrand: 'Retin-A',
    manufacturer: 'Bausch Health',
    retailPrice: 52,
    unit: 'tube',
    dosage: '0.05% / 20g',
    source: 'Generic tretinoin cream @ US chain pharmacy cash',
  },
  doxycycline: {
    usaBrand: 'Vibramycin',
    manufacturer: 'Pfizer',
    retailPrice: 2,
    unit: 'capsule',
    dosage: '100mg',
    source: 'Generic doxycycline @ US chain pharmacy cash',
  },
  minocycline: {
    usaBrand: 'Minocin',
    manufacturer: 'Onset Dermatologics',
    retailPrice: 2,
    unit: 'capsule',
    dosage: '100mg',
    source: 'Generic minocycline @ US chain pharmacy cash',
  },

  // ─── Weight Management ───
  rybelsus: {
    usaBrand: 'Rybelsus',
    manufacturer: 'Novo Nordisk',
    retailPrice: 28,
    unit: 'tablet',
    dosage: '14mg',
    source: 'Brand Rybelsus w/ Novo savings card — no US generic',
    isBrandBaseline: true,
  },
  // Injection pens — semaglutide (Ozempic/Wegovy equivalent)
  'semaglutide-pen-low': {
    usaBrand: 'Ozempic',
    manufacturer: 'Novo Nordisk',
    retailPrice: 349,
    unit: 'pen',
    dosage: '0.25–1mg',
    source: 'Ozempic direct price ($349/month 2026) — no US generic',
    isBrandBaseline: true,
  },
  'semaglutide-pen-high': {
    usaBrand: 'Wegovy',
    manufacturer: 'Novo Nordisk',
    retailPrice: 499,
    unit: 'pen',
    dosage: '1.7–2.4mg',
    source: 'Wegovy NovoCare direct ($499/month 2026) — no US generic',
    isBrandBaseline: true,
  },
  'fat-blocker': {
    usaBrand: 'Xenical',
    manufacturer: 'Roche',
    retailPrice: 8,
    unit: 'capsule',
    dosage: '120mg',
    source: 'Generic orlistat @ US chain pharmacy cash',
  },
  topiramate: {
    usaBrand: 'Topamax',
    manufacturer: 'Janssen',
    retailPrice: 1.50,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Generic topiramate @ US chain pharmacy cash',
  },

  // ─── Diabetes ───
  sitagliptin: {
    usaBrand: 'Januvia',
    manufacturer: 'Merck',
    retailPrice: 18,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Brand Januvia — no US generic yet',
    isBrandBaseline: true,
  },
  dapagliflozin: {
    usaBrand: 'Farxiga',
    manufacturer: 'AstraZeneca',
    retailPrice: 16,
    unit: 'tablet',
    dosage: '10mg',
    source: 'Brand Farxiga — no US generic yet',
    isBrandBaseline: true,
  },
  empagliflozin: {
    usaBrand: 'Jardiance',
    manufacturer: 'Boehringer Ingelheim',
    retailPrice: 18,
    unit: 'tablet',
    dosage: '25mg',
    source: 'Brand Jardiance — no US generic yet',
    isBrandBaseline: true,
  },
  metformin: {
    usaBrand: 'Glucophage',
    manufacturer: 'Merck',
    retailPrice: 0.90,
    unit: 'tablet',
    dosage: '500mg',
    source: 'Generic metformin @ US chain pharmacy cash',
  },
  sulfonylureas: {
    usaBrand: 'Amaryl',
    manufacturer: 'Sanofi',
    retailPrice: 0.50,
    unit: 'tablet',
    dosage: '2mg',
    source: 'Generic glimepiride @ US chain pharmacy cash',
  },

  // ─── Heart / Blood Pressure ───
  // Most of these are on Walmart $4 list → our savings story is modest
  // here. UI auto-hides the card when our price >= benchmark.
  'ace-inhibitors': {
    usaBrand: 'Vasotec',
    manufacturer: 'Bausch Health',
    retailPrice: 0.50,
    unit: 'tablet',
    dosage: '10mg',
    source: 'Generic enalapril @ US chain pharmacy cash',
  },
  arb: {
    usaBrand: 'Diovan',
    manufacturer: 'Novartis',
    retailPrice: 1,
    unit: 'tablet',
    dosage: '80mg',
    source: 'Generic valsartan/losartan @ US chain pharmacy cash',
  },
  'beta-blockers': {
    usaBrand: 'Lopressor',
    manufacturer: 'Novartis',
    retailPrice: 0.50,
    unit: 'tablet',
    dosage: '50mg',
    source: 'Generic metoprolol @ US chain pharmacy cash',
  },
  'calcium-channel': {
    usaBrand: 'Norvasc',
    manufacturer: 'Pfizer',
    retailPrice: 0.70,
    unit: 'tablet',
    dosage: '5mg',
    source: 'Generic amlodipine @ US chain pharmacy cash',
  },
  cholesterol: {
    usaBrand: 'Lipitor',
    manufacturer: 'Pfizer',
    retailPrice: 0.70,
    unit: 'tablet',
    dosage: '20mg',
    source: 'Generic atorvastatin @ US chain pharmacy cash',
  },
  antiplatelets: {
    usaBrand: 'Plavix',
    manufacturer: 'Bristol-Myers Squibb',
    retailPrice: 1,
    unit: 'tablet',
    dosage: '75mg',
    source: 'Generic clopidogrel @ US chain pharmacy cash',
  },
  'heart-diuretic': {
    usaBrand: 'Lasix',
    manufacturer: 'Sanofi',
    retailPrice: 0.30,
    unit: 'tablet',
    dosage: '40mg',
    source: 'Generic furosemide @ US chain pharmacy cash',
  },
  'heart-alpha-blocker': {
    usaBrand: 'Hytrin',
    manufacturer: 'Abbott',
    retailPrice: 0.70,
    unit: 'tablet',
    dosage: '5mg',
    source: 'Generic terazosin @ US chain pharmacy cash',
  },

  // ─── Antibiotics ───
  penicillin: {
    usaBrand: 'Amoxil',
    manufacturer: 'GSK',
    retailPrice: 0.70,
    unit: 'capsule',
    dosage: '500mg',
    source: 'Generic amoxicillin @ US chain pharmacy cash',
  },
  cephalosporin: {
    usaBrand: 'Keflex',
    manufacturer: 'Shionogi',
    retailPrice: 1.50,
    unit: 'capsule',
    dosage: '500mg',
    source: 'Generic cephalexin @ US chain pharmacy cash',
  },
  macrolide: {
    usaBrand: 'Zithromax',
    manufacturer: 'Pfizer',
    retailPrice: 6,
    unit: 'tablet',
    dosage: '250mg',
    source: 'Generic azithromycin (Z-pak course ~$32) @ US chain cash',
  },
  fluoroquinolone: {
    usaBrand: 'Cipro',
    manufacturer: 'Bayer',
    retailPrice: 2,
    unit: 'tablet',
    dosage: '500mg',
    source: 'Generic ciprofloxacin @ US chain pharmacy cash',
  },
  antifungal: {
    usaBrand: 'Diflucan',
    manufacturer: 'Pfizer',
    retailPrice: 15,
    unit: 'tablet',
    dosage: '150mg',
    source: 'Generic fluconazole @ US chain pharmacy cash',
  },
  'antibiotics-antiviral': {
    usaBrand: 'Zovirax',
    manufacturer: 'GSK',
    retailPrice: 100,
    unit: 'tube',
    dosage: '5% cream / 5g',
    source: 'Generic acyclovir cream @ US chain pharmacy cash',
  },

  // ─── Respiratory ───
  antihistamines: {
    usaBrand: 'Allegra',
    manufacturer: 'Sanofi',
    retailPrice: 1,
    unit: 'tablet',
    dosage: '180mg',
    source: 'Generic fexofenadine OTC retail',
  },
  leukotriene: {
    usaBrand: 'Singulair',
    manufacturer: 'Merck',
    retailPrice: 4,
    unit: 'tablet',
    dosage: '10mg',
    source: 'Generic montelukast @ US chain pharmacy cash',
  },
  bronchodilators: {
    usaBrand: 'Ventolin HFA',
    manufacturer: 'GSK',
    retailPrice: 40,
    unit: 'inhaler',
    dosage: '90mcg',
    source: 'Generic albuterol HFA @ US chain pharmacy cash',
  },
  corticosteroids: {
    usaBrand: 'Flovent HFA',
    manufacturer: 'GSK',
    retailPrice: 260,
    unit: 'inhaler',
    dosage: '110mcg',
    source: 'Generic fluticasone propionate HFA @ US chain pharmacy cash',
  },

  // ─── Antiparasitic ───
  // Specialty drugs — still pricey even as generics. Several have no
  // widely-available US generic, so brand baseline applies.
  ivermectin: {
    usaBrand: 'Stromectol',
    manufacturer: 'Merck',
    retailPrice: 30,
    unit: 'tablet',
    dosage: '12mg',
    source: 'Generic ivermectin @ US chain pharmacy cash',
  },
  albendazole: {
    usaBrand: 'Albenza',
    manufacturer: 'GSK',
    retailPrice: 50,
    unit: 'tablet',
    dosage: '400mg',
    source: 'Generic albendazole @ US chain pharmacy cash',
  },
  mebendazole: {
    usaBrand: 'Vermox',
    manufacturer: 'Janssen',
    retailPrice: 220,
    unit: 'tablet',
    dosage: '100mg',
    source: 'Brand Vermox/Emverm — no widely available US generic',
    isBrandBaseline: true,
  },
  praziquantel: {
    usaBrand: 'Biltricide',
    manufacturer: 'Bayer',
    retailPrice: 45,
    unit: 'tablet',
    dosage: '600mg',
    source: 'Brand Biltricide — limited US distribution',
    isBrandBaseline: true,
  },
  nitazoxanide: {
    usaBrand: 'Alinia',
    manufacturer: 'Romark',
    retailPrice: 200,
    unit: 'tablet',
    dosage: '500mg',
    source: 'Brand Alinia — no US generic',
    isBrandBaseline: true,
  },

  // ─── Hormones ───
  thyroid: {
    usaBrand: 'Synthroid',
    manufacturer: 'AbbVie',
    retailPrice: 0.50,
    unit: 'tablet',
    dosage: '50mcg',
    source: 'Generic levothyroxine @ US chain pharmacy cash',
  },
  'male-hormones': {
    usaBrand: 'Androgel',
    manufacturer: 'AbbVie',
    retailPrice: 12,
    unit: 'packet',
    dosage: '50mg',
    source: 'Generic testosterone gel @ US chain pharmacy cash',
  },
};

export function lookupBenchmark(subcategory: string | null | undefined): PriceBenchmark | null {
  if (!subcategory) return null;
  return BENCHMARKS[subcategory] || null;
}

// Product-aware benchmark lookup. Handles form-based differentiation
// (e.g., semaglutide tablets use Rybelsus $28, semaglutide pens use
// Ozempic $349 or Wegovy $499 depending on dose).
export function lookupBenchmarkForProduct(product: {
  id?: string;
  name?: string | null;
  categories?: string[] | null;
  pill_quantity?: number | null;
}): PriceBenchmark | null {
  const subcat = (product.categories || [])[1];
  const name = (product.name || '').toLowerCase();
  const isPen = name.includes('pen') || product.pill_quantity === 1;

  // Semaglutide pens — distinguish Ozempic (low dose) vs Wegovy (high dose)
  if ((subcat === 'wegovy' || subcat === 'rybelsus') && isPen) {
    const doseMatch = name.match(/(\d+(?:\.\d+)?)\s*mg/);
    const dose = doseMatch ? parseFloat(doseMatch[1]) : 0;
    return dose > 1 ? BENCHMARKS['semaglutide-pen-high'] : BENCHMARKS['semaglutide-pen-low'];
  }

  return lookupBenchmark(subcat);
}

// Compute cheapest per-item price from tiered pricing, accounting for
// pill_quantity (items per container). If pill_quantity > 0, each tier's
// quantity is a container count and we divide further by items per container.
// Returns the cheapest per-ITEM price (per tablet/capsule/sachet).
export function ourCheapestPerUnit(
  prices: { quantity: number; sale_price: number }[],
  pillQuantity: number = 0
): number {
  if (!prices.length) return 0;
  const itemsPerQty = pillQuantity > 0 ? pillQuantity : 1;
  let best = Infinity;
  for (const p of prices) {
    const totalItems = p.quantity * itemsPerQty;
    if (totalItems <= 0) continue;
    const perItem = p.sale_price / totalItems;
    if (perItem < best) best = perItem;
  }
  return best === Infinity ? 0 : best;
}
